import React, { useState, useEffect, useCallback } from 'react';
import { MultiSelectFilter } from '../../components/MultiSelectFilter';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card } from '../../components/ui/card';
import { resolveFileUrl } from '../../utils/fileUrl';
import { toast } from 'sonner';
import PaginationBar from '../../components/PaginationBar';
import {
  Search, User, MapPin, GraduationCap, Briefcase, Heart,
  Loader2, ChevronLeft, ChevronRight, X, Calendar, Ruler,
  Users, BookOpen, Filter
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const calculateAge = (dob) => {
  if (!dob) return '';
  try {
    const birth = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birth.getFullYear();
    if (today.getMonth() < birth.getMonth() || (today.getMonth() === birth.getMonth() && today.getDate() < birth.getDate())) age--;
    return age > 0 ? `${age} yrs` : '';
  } catch { return ''; }
};

const InfoRow = ({ icon, label, value }) => {
  if (!value) return null;
  return (
    <div className="flex items-start gap-2 py-1.5">
      <span className="text-gray-400 mt-0.5">{icon}</span>
      <div>
        <p className="text-xs text-gray-500">{label}</p>
        <p className="text-sm font-medium text-gray-800">{value}</p>
      </div>
    </div>
  );
};

const CandidateCard = ({ candidate, onClick }) => {
  const age = calculateAge(candidate.dateOfBirth);
  const gender = (candidate.gender || '').toLowerCase().includes('male') && !(candidate.gender || '').toLowerCase().includes('female') ? 'male' : 'female';
  const borderColor = gender === 'male' ? 'border-sky-200 hover:border-sky-400' : 'border-pink-200 hover:border-pink-400';
  const badgeColor = gender === 'male' ? 'bg-sky-100 text-sky-700' : 'bg-pink-100 text-pink-700';

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -2 }}
      transition={{ duration: 0.2 }}
    >
      <Card
        className={`cursor-pointer border-2 ${borderColor} transition-all overflow-hidden relative ${candidate.isEngaged ? 'opacity-95' : ''}`}
        onClick={onClick}
        data-testid={`directory-card-${candidate.candidateCode}`}
      >
        {candidate.isEngaged && (
          <div className="absolute top-2 right-2 z-10 bg-gradient-to-r from-pink-600 to-rose-600 text-white text-[10px] font-bold px-2 py-1 rounded-full shadow-md flex items-center gap-1" data-testid={`engaged-badge-${candidate.candidateCode}`}>
            <Heart className="w-3 h-3 fill-current" />
            ENGAGED
          </div>
        )}
        <div className="p-4">
          <div className="flex gap-3">
            {/* Photo */}
            <div className="w-20 h-24 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0">
              {candidate.photoUrl ? (
                <img src={resolveFileUrl(candidate.photoUrl)} alt={candidate.name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <User className="w-8 h-8 text-gray-300" />
                </div>
              )}
            </div>
            {/* Info */}
            <div className="flex-1 min-w-0">
              <h3 className="font-bold text-gray-900 text-sm truncate">{candidate.name}</h3>
              <p className="text-xs text-gray-500 truncate">S/D of {candidate.fatherName}</p>
              <div className="flex items-center gap-2 mt-1 flex-wrap">
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${badgeColor}`}>
                  {candidate.gender?.split('/')[0]?.trim()}
                </span>
                {age && <span className="text-xs text-gray-500">{age}</span>}
              </div>
              {candidate.nukh && <p className="text-xs text-gray-600 mt-1">Nukh: {candidate.nukh}</p>}
              <div className="flex items-center gap-1 mt-1">
                <MapPin className="w-3 h-3 text-gray-400" />
                <span className="text-xs text-gray-600 truncate">{candidate.currentPlace}{candidate.currentState ? `, ${candidate.currentState}` : ''}</span>
              </div>
              {candidate.education && (
                <div className="flex items-center gap-1 mt-0.5">
                  <GraduationCap className="w-3 h-3 text-gray-400" />
                  <span className="text-xs text-gray-600 truncate">{candidate.education}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </Card>
    </motion.div>
  );
};

const CandidateDetail = ({ candidate, onClose }) => {
  const [contactInfo, setContactInfo] = useState(null);
  const [contactLoading, setContactLoading] = useState(false);
  const [contactRevealed, setContactRevealed] = useState(false);
  const [photoLightbox, setPhotoLightbox] = useState(false);

  const handleRevealContact = async () => {
    if (contactRevealed) return;
    setContactLoading(true);
    try {
      const token = (
        localStorage.getItem('psToken') ||
        localStorage.getItem('familyToken') ||
        localStorage.getItem('professionalToken') ||
        localStorage.getItem('institutionalToken') ||
        localStorage.getItem('token')
      );
      const res = await fetch(`${BACKEND_URL}/api/ps/directory/${candidate.candidateCode}/contact`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setContactInfo(data);
        setContactRevealed(true);
        toast.success('Contact details revealed');
      } else if (res.status === 409) {
        const data = await res.json().catch(() => ({}));
        toast.info(data.detail || 'This candidate is already engaged.');
      } else {
        toast.error('Failed to fetch contact details');
      }
    } catch {
      toast.error('Failed to fetch contact details');
    } finally {
      setContactLoading(false);
    }
  };

  if (!candidate) return null;
  const age = calculateAge(candidate.dateOfBirth);
  const gender = (candidate.gender || '').toLowerCase().includes('male') && !(candidate.gender || '').toLowerCase().includes('female') ? 'male' : 'female';
  const accentColor = gender === 'male' ? 'bg-sky-600' : 'bg-pink-600';

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="directory-detail-modal"
      >
        {/* ENGAGED top-banner */}
        {candidate.isEngaged && (
          <div className="bg-gradient-to-r from-pink-600 via-rose-600 to-pink-600 text-white py-3 px-5 text-center flex items-center justify-center gap-2 shadow-inner" data-testid="engaged-top-banner">
            <Heart className="w-5 h-5 fill-white" />
            <span className="font-bold tracking-wide">Parichay Ho Gaya! — ENGAGED</span>
            <Heart className="w-5 h-5 fill-white" />
          </div>
        )}
        {/* Header */}
        <div className={`${accentColor} text-white p-5 ${candidate.isEngaged ? '' : 'rounded-t-xl'} relative`}>
          <button onClick={onClose} className="absolute top-3 right-3 p-1.5 bg-white/20 rounded-full hover:bg-white/30">
            <X className="w-4 h-4" />
          </button>
          <div className="flex items-center gap-4">
            <div
              className={`w-24 h-28 rounded-lg overflow-hidden bg-white/20 flex-shrink-0 border-2 border-white/30 ${candidate.photoUrl ? 'cursor-zoom-in hover:border-white/70 transition-all' : ''}`}
              onClick={() => candidate.photoUrl && setPhotoLightbox(true)}
              data-testid="directory-detail-photo-thumb"
              title={candidate.photoUrl ? 'Click to view full photo' : ''}
            >
              {candidate.photoUrl ? (
                <img src={resolveFileUrl(candidate.photoUrl)} alt={candidate.name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center"><User className="w-10 h-10 text-white/50" /></div>
              )}
            </div>
            <div>
              <h2 className="text-xl font-bold">{candidate.name}</h2>
              <p className="text-sm text-white/80">S/D of {candidate.fatherName}</p>
              <div className="flex items-center gap-2 mt-1">
                <span className="text-xs bg-white/20 px-2 py-0.5 rounded-full">{candidate.gender?.split('/')[0]?.trim()}</span>
                {age && <span className="text-xs text-white/80">{age}</span>}
                <span className="text-xs text-white/60">{candidate.candidateCode}</span>
              </div>
              {candidate.photoUrl && (
                <button
                  onClick={() => setPhotoLightbox(true)}
                  className="mt-2 text-[11px] text-white/90 underline underline-offset-2 hover:text-white"
                  data-testid="directory-detail-view-full-photo"
                >
                  View full photo
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="p-5 space-y-5">
          {/* Personal */}
          <div>
            <h3 className="text-sm font-bold text-gray-700 mb-2 uppercase tracking-wider border-b pb-1">Personal Details</h3>
            <div className="grid grid-cols-2 gap-x-6 gap-y-1">
              <InfoRow icon={<User className="w-3.5 h-3.5" />} label="Mother's Name" value={candidate.motherName} />
              <InfoRow icon={<Users className="w-3.5 h-3.5" />} label="Nukh" value={candidate.nukh} />
              <InfoRow icon={<Calendar className="w-3.5 h-3.5" />} label="Date of Birth" value={candidate.dateOfBirth ? new Date(candidate.dateOfBirth).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : ''} />
              <InfoRow icon={<MapPin className="w-3.5 h-3.5" />} label="Birth Place" value={candidate.birthPlace} />
              <InfoRow icon={<Ruler className="w-3.5 h-3.5" />} label="Height" value={candidate.height} />
              <InfoRow icon={<Ruler className="w-3.5 h-3.5" />} label="Weight" value={candidate.weight} />
              <InfoRow icon={<Heart className="w-3.5 h-3.5" />} label="Complexion" value={candidate.complexion} />
              <InfoRow icon={<Heart className="w-3.5 h-3.5" />} label="Blood Group" value={candidate.bloodGroup} />
              <InfoRow icon={<Heart className="w-3.5 h-3.5" />} label="Marital Status" value={candidate.maritalStatus} />
              <InfoRow icon={<Heart className="w-3.5 h-3.5" />} label="Manglik" value={candidate.mapiOrManglik} />
            </div>
          </div>

          {/* Education & Career */}
          <div>
            <h3 className="text-sm font-bold text-gray-700 mb-2 uppercase tracking-wider border-b pb-1">Education & Career</h3>
            <div className="grid grid-cols-2 gap-x-6 gap-y-1">
              <InfoRow icon={<GraduationCap className="w-3.5 h-3.5" />} label="Education" value={candidate.education} />
              <InfoRow icon={<Briefcase className="w-3.5 h-3.5" />} label="Occupation" value={candidate.occupation} />
              <InfoRow icon={<Briefcase className="w-3.5 h-3.5" />} label="Working At" value={candidate.workingAt} />
              <InfoRow icon={<Briefcase className="w-3.5 h-3.5" />} label="Annual Income" value={candidate.annualIncome} />
            </div>
          </div>

          {/* Location */}
          <div>
            <h3 className="text-sm font-bold text-gray-700 mb-2 uppercase tracking-wider border-b pb-1">Location</h3>
            <div className="grid grid-cols-2 gap-x-6 gap-y-1">
              <InfoRow icon={<MapPin className="w-3.5 h-3.5" />} label="Current Place" value={[candidate.currentPlace, candidate.currentState, candidate.currentCountry].filter(Boolean).join(', ')} />
              <InfoRow icon={<MapPin className="w-3.5 h-3.5" />} label="Current Address" value={candidate.currentAddress} />
              <InfoRow icon={<MapPin className="w-3.5 h-3.5" />} label="Native Place" value={[candidate.nativePlace, candidate.nativeState].filter(Boolean).join(', ')} />
            </div>
          </div>

          {/* Maternal */}
          <div>
            <h3 className="text-sm font-bold text-gray-700 mb-2 uppercase tracking-wider border-b pb-1">Maternal Details</h3>
            <div className="grid grid-cols-2 gap-x-6 gap-y-1">
              <InfoRow icon={<Users className="w-3.5 h-3.5" />} label="Maternal Name" value={candidate.maternalName} />
              <InfoRow icon={<Users className="w-3.5 h-3.5" />} label="Maternal Nukh" value={candidate.nukhOfMaternals} />
              <InfoRow icon={<MapPin className="w-3.5 h-3.5" />} label="Maternal Place" value={candidate.placeOfMaternals} />
            </div>
          </div>

          {/* Siblings */}
          {(candidate.unmarriedBrothers || candidate.unmarriedSisters || candidate.marriedBrothers || candidate.marriedSisters) && (
            <div>
              <h3 className="text-sm font-bold text-gray-700 mb-2 uppercase tracking-wider border-b pb-1">Siblings</h3>
              <div className="grid grid-cols-2 gap-x-6 gap-y-1">
                <InfoRow icon={<Users className="w-3.5 h-3.5" />} label="Unmarried Brothers" value={candidate.unmarriedBrothers} />
                <InfoRow icon={<Users className="w-3.5 h-3.5" />} label="Unmarried Sisters" value={candidate.unmarriedSisters} />
                <InfoRow icon={<Users className="w-3.5 h-3.5" />} label="Married Brothers" value={candidate.marriedBrothers} />
                <InfoRow icon={<Users className="w-3.5 h-3.5" />} label="Married Sisters" value={candidate.marriedSisters} />
              </div>
            </div>
          )}

          {/* About & Preferences */}
          {(candidate.aboutCandidate || candidate.hobbies || candidate.partnerPreference) && (
            <div>
              <h3 className="text-sm font-bold text-gray-700 mb-2 uppercase tracking-wider border-b pb-1">About & Preferences</h3>
              {candidate.aboutCandidate && (
                <div className="mb-2">
                  <p className="text-xs text-gray-500">About</p>
                  <p className="text-sm text-gray-800">{candidate.aboutCandidate}</p>
                </div>
              )}
              {candidate.hobbies && (
                <div className="mb-2">
                  <p className="text-xs text-gray-500">Hobbies</p>
                  <p className="text-sm text-gray-800">{candidate.hobbies}</p>
                </div>
              )}
              {candidate.partnerPreference && (
                <div>
                  <p className="text-xs text-gray-500">Partner Preference</p>
                  <p className="text-sm text-gray-800">{candidate.partnerPreference}</p>
                </div>
              )}
            </div>
          )}

          {/* Contact / Express Interest */}
          <div className="pt-3 border-t">
            {candidate.isEngaged ? (
              <div className="bg-gradient-to-r from-pink-50 to-rose-50 border-2 border-pink-300 rounded-xl p-5 text-center" data-testid="engaged-banner">
                <div className="flex items-center justify-center gap-2 mb-1">
                  <Heart className="w-5 h-5 fill-pink-600 text-pink-600" />
                  <span className="text-pink-700 font-bold text-base tracking-wide">Parichay Ho Gaya!</span>
                  <Heart className="w-5 h-5 fill-pink-600 text-pink-600" />
                </div>
                <p className="text-sm font-bold text-pink-700 uppercase tracking-widest">Engaged</p>
                <p className="text-xs text-pink-600 mt-2">परिचय हो गया — best wishes to the family / परिवार को हार्दिक शुभकामनाएं</p>
              </div>
            ) : !contactRevealed ? (
              <>
                <Button
                  className="w-full bg-orange-600 hover:bg-orange-700"
                  onClick={handleRevealContact}
                  disabled={contactLoading}
                  data-testid="express-interest-btn"
                >
                  {contactLoading ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Fetching...</>
                  ) : (
                    <><Heart className="w-4 h-4 mr-2" /> Express Interest / रुचि व्यक्त करें</>
                  )}
                </Button>
                <p className="text-xs text-gray-500 mt-2 text-center">
                  Click to reveal contact details / संपर्क विवरण देखने के लिए क्लिक करें
                </p>
              </>
            ) : (
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4" data-testid="contact-info-section">
                <h3 className="text-sm font-bold text-orange-800 mb-3 flex items-center gap-2">
                  <Heart className="w-4 h-4 fill-orange-600 text-orange-600" />
                  Contact Information / संपर्क विवरण
                </h3>
                <div className="space-y-2 text-sm">
                  {contactInfo?.candidatePhone && (
                    <div className="flex items-center justify-between bg-white px-3 py-2 rounded border border-orange-100">
                      <span className="text-xs text-gray-500">Candidate Phone</span>
                      <a href={`tel:${contactInfo.candidatePhone}`} className="font-semibold text-orange-700 hover:underline" data-testid="contact-candidate-phone">{contactInfo.candidatePhone}</a>
                    </div>
                  )}
                  {contactInfo?.candidateWhatsapp && (
                    <div className="flex items-center justify-between bg-white px-3 py-2 rounded border border-orange-100">
                      <span className="text-xs text-gray-500">WhatsApp</span>
                      <a href={`https://wa.me/${contactInfo.candidateWhatsapp.replace(/\D/g, '')}`} target="_blank" rel="noopener noreferrer" className="font-semibold text-green-600 hover:underline">{contactInfo.candidateWhatsapp}</a>
                    </div>
                  )}
                  {contactInfo?.candidateEmail && (
                    <div className="flex items-center justify-between bg-white px-3 py-2 rounded border border-orange-100">
                      <span className="text-xs text-gray-500">Candidate Email</span>
                      <a href={`mailto:${contactInfo.candidateEmail}`} className="font-semibold text-orange-700 hover:underline truncate ml-2">{contactInfo.candidateEmail}</a>
                    </div>
                  )}
                  {contactInfo?.guardianName && (
                    <div className="flex items-center justify-between bg-white px-3 py-2 rounded border border-orange-100">
                      <span className="text-xs text-gray-500">Guardian</span>
                      <span className="font-semibold text-gray-800">{contactInfo.guardianName}</span>
                    </div>
                  )}
                  {contactInfo?.guardianWhatsapp && (
                    <div className="flex items-center justify-between bg-white px-3 py-2 rounded border border-orange-100">
                      <span className="text-xs text-gray-500">Guardian WhatsApp</span>
                      <a href={`https://wa.me/${contactInfo.guardianWhatsapp.replace(/\D/g, '')}`} target="_blank" rel="noopener noreferrer" className="font-semibold text-green-600 hover:underline">{contactInfo.guardianWhatsapp}</a>
                    </div>
                  )}
                  {contactInfo?.familyEmail && contactInfo.familyEmail !== contactInfo.candidateEmail && (
                    <div className="flex items-center justify-between bg-white px-3 py-2 rounded border border-orange-100">
                      <span className="text-xs text-gray-500">Family Email</span>
                      <a href={`mailto:${contactInfo.familyEmail}`} className="font-semibold text-orange-700 hover:underline truncate ml-2">{contactInfo.familyEmail}</a>
                    </div>
                  )}
                  {!contactInfo?.candidatePhone && !contactInfo?.candidateWhatsapp && !contactInfo?.candidateEmail && !contactInfo?.guardianWhatsapp && !contactInfo?.familyEmail && (
                    <p className="text-sm text-gray-600">No direct contact information available. Please coordinate through the organizing committee.</p>
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-3 italic">
                  Please respect privacy and use this information responsibly / कृपया गोपनीयता का सम्मान करें।
                </p>
              </div>
            )}
          </div>
        </div>
      </motion.div>

      {/* Photo Lightbox */}
      {photoLightbox && candidate.photoUrl && (
        <div
          className="fixed inset-0 bg-black/90 z-[60] flex items-center justify-center p-4"
          onClick={(e) => { e.stopPropagation(); setPhotoLightbox(false); }}
          data-testid="directory-detail-photo-lightbox"
        >
          <button
            onClick={(e) => { e.stopPropagation(); setPhotoLightbox(false); }}
            className="absolute top-4 right-4 p-2 bg-white/10 hover:bg-white/20 rounded-full text-white"
            data-testid="directory-detail-photo-lightbox-close"
          >
            <X className="w-5 h-5" />
          </button>
          <img
            src={resolveFileUrl(candidate.photoUrl)}
            alt={candidate.name}
            className="max-w-full max-h-[90vh] object-contain rounded-lg shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
};

const PSMatrimonialDirectory = () => {
  const navigate = useNavigate();
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({
    gender: '',
    nukh: [],
    place: [],
    education: [],
    maritalStatus: [],
    ageMin: '',
    ageMax: '',
  });
  const [filterOptions, setFilterOptions] = useState({ nukhs: [], places: [], educations: [], genders: [], maritalStatuses: [] });
  const [showFilters, setShowFilters] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  // Tracks whether we've initialised the multi-select filters with all options
  // (so we don't accidentally re-fill them after the user explicitly deselects items).
  const [optionsInitialized, setOptionsInitialized] = useState(false);

  const token = (
    localStorage.getItem('psToken') ||
    localStorage.getItem('familyToken') ||
    localStorage.getItem('professionalToken') ||
    localStorage.getItem('institutionalToken') ||
    localStorage.getItem('token')
  );
  const isAdminBypass = !localStorage.getItem('psToken') && !!localStorage.getItem('token') &&
    !localStorage.getItem('familyToken') && !localStorage.getItem('professionalToken') && !localStorage.getItem('institutionalToken');

  useEffect(() => {
    if (!token) {
      toast.error('Please login to view the directory');
      navigate('/parichay-sangam/login?next=/parichay-sangam/directory');
      return;
    }
    fetch(`${BACKEND_URL}/api/ps/directory/options/filters`, {
      headers: { 'Authorization': `Bearer ${token}` },
    })
      .then(r => { if (!r.ok) throw new Error('Auth'); return r.json(); })
      .then(d => {
        setFilterOptions(d);
        // Default all multi-selects to "all selected" so the directory
        // shows everyone until the user deselects specific options.
        if (!optionsInitialized) {
          setFilters(f => ({
            ...f,
            nukh: Array.isArray(d?.nukhs) ? [...d.nukhs] : [],
            place: Array.isArray(d?.places) ? [...d.places] : [],
            education: Array.isArray(d?.educations) ? [...d.educations] : [],
            maritalStatus: Array.isArray(d?.maritalStatuses) ? [...d.maritalStatuses] : [],
          }));
          setOptionsInitialized(true);
        }
      })
      .catch(() => { toast.error('Please login to access directory'); navigate('/parichay-sangam/login?next=/parichay-sangam/directory'); });
  }, [token, navigate, optionsInitialized]);

  const fetchCandidates = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: page.toString(), limit: '20' });
      if (search) params.append('search', search);
      if (filters.gender) params.append('gender', filters.gender);

      // Multi-select fields. For each one:
      //  - 0 selected → send sentinel `__none__` (return 0 matches)
      //  - subset selected → send each value
      //  - all selected (or options still loading) → skip param entirely
      const appendMulti = (key, totalOptions) => {
        const sel = filters[key];
        if (!Array.isArray(sel) || totalOptions.length === 0) return;
        if (sel.length >= totalOptions.length) return; // all selected
        if (sel.length === 0) { params.append(key, '__none__'); return; }
        sel.forEach(v => params.append(key, v));
      };
      appendMulti('nukh', filterOptions.nukhs);
      appendMulti('place', filterOptions.places);
      appendMulti('education', filterOptions.educations);
      appendMulti('maritalStatus', filterOptions.maritalStatuses);

      // Age range — send only valid integers.
      const aMin = parseInt(filters.ageMin, 10);
      const aMax = parseInt(filters.ageMax, 10);
      if (!isNaN(aMin) && aMin > 0) params.append('ageMin', String(aMin));
      if (!isNaN(aMax) && aMax > 0) params.append('ageMax', String(aMax));

      const res = await fetch(`${BACKEND_URL}/api/ps/directory?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed');
      const data = await res.json();
      setCandidates(data.candidates || []);
      setTotal(data.total || 0);
      setTotalPages(data.totalPages || 1);
    } catch {
      toast.error('Failed to load directory');
    } finally {
      setLoading(false);
    }
  }, [page, search, filters, token, filterOptions]);

  useEffect(() => { fetchCandidates(); }, [fetchCandidates]);

  const openDetail = async (candidateCode) => {
    setDetailLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/ps/directory/${candidateCode}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setSelectedCandidate(data);
      }
    } catch { toast.error('Failed to load profile'); }
    finally { setDetailLoading(false); }
  };

  const clearFilters = () => {
    setFilters({
      gender: '',
      nukh: [...(filterOptions.nukhs || [])],
      place: [...(filterOptions.places || [])],
      education: [...(filterOptions.educations || [])],
      maritalStatus: [...(filterOptions.maritalStatuses || [])],
      ageMin: '',
      ageMax: '',
    });
    setSearch('');
    setPage(1);
  };

  // A multi-select is "active" when the user has narrowed the selection.
  const isNarrowed = (selKey, total) =>
    Array.isArray(filters[selKey]) && total > 0 && filters[selKey].length < total;

  const nukhFilterActive = isNarrowed('nukh', filterOptions.nukhs.length);
  const placeFilterActive = isNarrowed('place', filterOptions.places.length);
  const eduFilterActive = isNarrowed('education', filterOptions.educations.length);
  const maritalFilterActive = isNarrowed('maritalStatus', filterOptions.maritalStatuses.length);
  const ageFilterActive = !!filters.ageMin || !!filters.ageMax;

  const setMulti = (key) => (next) => {
    setFilters(f => ({ ...f, [key]: next }));
    setPage(1);
  };

  const hasActiveFilters = search || filters.gender ||
    nukhFilterActive || placeFilterActive || eduFilterActive || maritalFilterActive || ageFilterActive;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <section className="gradient-orange py-8">
        <div className="max-w-6xl mx-auto px-4">
          <h1 className="text-2xl md:text-3xl font-bold text-white mb-1">Parichay Sangam 2026 Directory</h1>
          <p className="text-white/80 text-sm">Browse matrimonial profiles / वैवाहिक प्रोफाइल देखें</p>
          {isAdminBypass && (
            <div
              className="mt-3 inline-flex items-center gap-2 bg-white/90 text-orange-700 text-xs font-semibold px-3 py-1.5 rounded-full shadow-sm"
              data-testid="admin-preview-badge"
            >
              <span className="w-2 h-2 bg-orange-500 rounded-full animate-pulse" />
              Admin Preview Mode — visible to admins only
            </div>
          )}
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Search & Filters */}
        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            <Input
              placeholder="Search by name, nukh, place, email, candidate code, family code..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              className="pl-10"
              data-testid="ps-directory-search"
            />
          </div>
          <Button variant="outline" onClick={() => setShowFilters(!showFilters)} data-testid="ps-directory-filter-toggle">
            <Filter className="w-4 h-4 mr-1" /> Filters {hasActiveFilters && <span className="ml-1 w-2 h-2 bg-orange-500 rounded-full" />}
          </Button>
          {hasActiveFilters && (
            <Button variant="ghost" size="sm" onClick={clearFilters} className="text-gray-500">
              <X className="w-4 h-4 mr-1" /> Clear
            </Button>
          )}
        </div>

        {/* Filter Row */}
        <AnimatePresence>
          {showFilters && (
            <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden mb-4">
              <Card className="p-4">
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-12 gap-3">
                  <select value={filters.gender} onChange={(e) => { setFilters(f => ({ ...f, gender: e.target.value })); setPage(1); }} className="h-9 px-3 rounded-md border text-sm md:col-span-2">
                    <option value="">All Genders</option>
                    {filterOptions.genders.map(g => <option key={g} value={g}>{g}</option>)}
                  </select>
                  <div className="md:col-span-2">
                    <MultiSelectFilter
                      label="All Nukh"
                      options={filterOptions.nukhs}
                      selected={filters.nukh}
                      onChange={setMulti('nukh')}
                      testId="ps-nukh"
                      selectionLabel={(c, t) => `${c} of ${t} Nukh`}
                    />
                  </div>
                  <div className="md:col-span-2">
                    <MultiSelectFilter
                      label="All Places"
                      options={filterOptions.places}
                      selected={filters.place}
                      onChange={setMulti('place')}
                      testId="ps-place"
                      selectionLabel={(c, t) => `${c} of ${t} Places`}
                    />
                  </div>
                  <div className="md:col-span-2">
                    <MultiSelectFilter
                      label="All Education"
                      options={filterOptions.educations}
                      selected={filters.education}
                      onChange={setMulti('education')}
                      testId="ps-edu"
                      selectionLabel={(c, t) => `${c} of ${t} Education`}
                    />
                  </div>
                  <div className="md:col-span-2">
                    <MultiSelectFilter
                      label="All Status"
                      options={filterOptions.maritalStatuses}
                      selected={filters.maritalStatus}
                      onChange={setMulti('maritalStatus')}
                      testId="ps-marital"
                      selectionLabel={(c, t) => `${c} of ${t} Status`}
                    />
                  </div>
                  {/* Age Range — two compact number inputs */}
                  <div
                    className={`h-9 px-2 rounded-md border text-sm flex items-center gap-1 md:col-span-2 ${ageFilterActive ? 'border-orange-400 bg-orange-50' : 'border-gray-200 bg-white'}`}
                    data-testid="ps-age-range"
                    title="Filter by age range (years)"
                  >
                    <span className="text-xs font-medium text-gray-600 pr-1.5 border-r border-gray-200 mr-1 whitespace-nowrap">Age</span>
                    <input
                      type="number"
                      min="0"
                      max="120"
                      placeholder="Min"
                      value={filters.ageMin}
                      onChange={(e) => { setFilters(f => ({ ...f, ageMin: e.target.value })); setPage(1); }}
                      className="w-full min-w-0 text-sm bg-transparent outline-none placeholder:text-gray-400 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                      data-testid="ps-age-min"
                    />
                    <span className="text-gray-400 text-xs">–</span>
                    <input
                      type="number"
                      min="0"
                      max="120"
                      placeholder="Max"
                      value={filters.ageMax}
                      onChange={(e) => { setFilters(f => ({ ...f, ageMax: e.target.value })); setPage(1); }}
                      className="w-full min-w-0 text-sm bg-transparent outline-none placeholder:text-gray-400 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                      data-testid="ps-age-max"
                    />
                  </div>
                </div>
              </Card>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Results Count */}
        <p className="text-sm text-gray-500 mb-4">{total} candidate{total !== 1 ? 's' : ''} found</p>

        {/* Cards Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
          </div>
        ) : candidates.length === 0 ? (
          <div className="text-center py-16">
            <Users className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No candidates found</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {candidates.map((c) => (
              <CandidateCard key={c.candidateCode} candidate={c} onClick={() => openDetail(c.candidateCode)} />
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <PaginationBar
            currentPage={page}
            totalPages={totalPages}
            onPageChange={(p) => setPage(p)}
            testIdPrefix="ps-directory"
          />
        )}
      </div>

      {/* Detail Modal */}
      <AnimatePresence>
        {selectedCandidate && (
          <CandidateDetail candidate={selectedCandidate} onClose={() => setSelectedCandidate(null)} />
        )}
      </AnimatePresence>

      {/* Loading Overlay for detail */}
      {detailLoading && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-40">
          <Loader2 className="w-8 h-8 animate-spin text-white" />
        </div>
      )}
    </div>
  );
};

export default PSMatrimonialDirectory;
